import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Mail, Lock, Activity, Star, MailCheck, User as UserIcon, Ruler, Target, Camera } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { mediaUrl } from "@/components/ChatPanel";
import TrainingPeaksHelp from "@/components/TrainingPeaksHelp";

function Card({ icon: Icon, title, children }) {
  return (
    <div className="border border-white/10 bg-[#141414] p-3 sm:p-4">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] mb-2 sm:mb-3 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {title}</p>
      {children}
    </div>
  );
}

export default function AccountSettings({ open, onOpenChange }) {
  const { email, user, refreshUser, distanceUnit, weightUnit, planGoalWeight } = useAuth();
  const [unitsBusy, setUnitsBusy] = useState(false);
  // Goal weight is stored canonically in kg, but the input mirrors the
  // athlete's preferred weight unit so they never have to convert.
  const kgToDisplay = (kg) => {
    const n = Number(kg);
    if (kg == null || !isFinite(n)) return "";
    if (weightUnit === "lb") return (n * 2.20462).toFixed(1);
    if (weightUnit === "st") return (n * 0.157473).toFixed(2);
    return n.toFixed(1);
  };
  const displayToKg = (v) => {
    const n = parseFloat(v);
    if (!isFinite(n)) return null;
    if (weightUnit === "lb") return +(n / 2.20462).toFixed(2);
    if (weightUnit === "st") return +(n / 0.157473).toFixed(2);
    return +n.toFixed(2);
  };
  const [goalWt, setGoalWt] = useState(kgToDisplay(planGoalWeight));
  const [goalWtBusy, setGoalWtBusy] = useState(false);
  // Weight-tracking cadence (Feb 26 2026 beta feedback: "some testers want
  // to track weight daily and others don't"). Stored on the user record
  // via /users/profile-defaults so it persists across devices + plans.
  const [wtCadence, setWtCadence] = useState(user?.weightTracking || "weekly");
  const [wtCadenceBusy, setWtCadenceBusy] = useState(false);
  useEffect(() => { setWtCadence(user?.weightTracking || "weekly"); }, [user?.weightTracking]);
  // Progress photos — opt-in body/progress tracking. Toggles the Progress
  // Photo tile inside Daily Snapshot. Off by default so athletes who don't
  // want it never see the prompt.
  const [progressOn, setProgressOn] = useState(!!user?.progressPhotosEnabled);
  const [progressBusy, setProgressBusy] = useState(false);
  useEffect(() => { setProgressOn(!!user?.progressPhotosEnabled); }, [user?.progressPhotosEnabled]);
  const toggleProgressPhotos = async (next) => {
    if (progressBusy) return;
    setProgressBusy(true);
    const prev = progressOn;
    setProgressOn(next);
    try {
      await api.post("/users/profile-defaults", { progressPhotos: next });
      await refreshUser();
      toast.success(next ? "Progress photos turned on" : "Progress photos turned off");
    } catch (err) {
      setProgressOn(prev);
      toast.error(apiError(err, "Could not update progress photos"));
    } finally { setProgressBusy(false); }
  };
  const saveWtCadence = async (next) => {
    if (next === wtCadence) return;
    setWtCadenceBusy(true);
    const prev = wtCadence;
    setWtCadence(next); // optimistic
    try {
      await api.post("/users/profile-defaults", { weightTracking: next });
      await refreshUser();
      const label = next === "daily" ? "every day"
        : next === "off" ? "hidden — you won't be prompted to weigh in"
        : "weekends only (Sat + Sun)";
      toast.success(`Weight tracking · ${label}`);
    } catch (err) {
      setWtCadence(prev);
      toast.error(apiError(err, "Could not update weight tracking preference"));
    } finally { setWtCadenceBusy(false); }
  };
  useEffect(() => { setGoalWt(kgToDisplay(planGoalWeight)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [planGoalWeight, weightUnit]);
  const saveUnits = async (patch) => {
    setUnitsBusy(true);
    try {
      await api.post("/settings/units", patch);
      await refreshUser();
      toast.success("Preferences saved");
    } catch (err) { toast.error(apiError(err, "Could not update preferences")); }
    finally { setUnitsBusy(false); }
  };
  const saveGoalWeight = async () => {
    const kg = displayToKg(goalWt);
    if (kg == null || !(kg >= 30 && kg <= 250)) { toast.error("Goal weight must be between 30 and 250 kg"); return; }
    setGoalWtBusy(true);
    try {
      await api.post("/settings/units", { goalWeight: kg });
      await refreshUser();
      toast.success(`Goal weight set — Weight card's "vs goal" updates instantly`);
    } catch (err) { toast.error(apiError(err, "Could not update goal weight")); }
    finally { setGoalWtBusy(false); }
  };
  const [displayName, setDisplayName] = useState("");
  const [nameBusy, setNameBusy] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [tp, setTp] = useState({ connected: false });
  const [tpUrl, setTpUrl] = useState("");
  const [tpBusy, setTpBusy] = useState(false);
  const [tppEmail, setTppEmail] = useState("");
  const [tppBusy, setTppBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Photo too large (2MB max)"); return; }
    setAvatarBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/users/avatar", form, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Profile photo updated");
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    finally { setAvatarBusy(false); }
  };
  const removeAvatar = async () => {
    setAvatarBusy(true);
    try { await api.delete("/users/avatar"); await refreshUser(); toast.success("Photo removed"); }
    catch (e) { toast.error(apiError(e, "Could not remove")); }
    finally { setAvatarBusy(false); }
  };
  const [recapEnabled, setRecapEnabled] = useState(true);
  const [recapLastSent, setRecapLastSent] = useState(null);
  const [recapBusy, setRecapBusy] = useState(false);
  const tpp = user?.tpPremium || null;
  const tppActive = tpp && ["pending", "active", "cancelling"].includes(tpp.status);

  const loadTp = useCallback(async () => {
    try { const { data } = await api.get("/integrations/trainingpeaks/events"); setTp({ connected: !!data.connected }); }
    catch { setTp({ connected: false }); }
  }, []);
  const loadRecap = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/weekly-recap");
      setRecapEnabled(!!data.enabled);
      setRecapLastSent(data.lastSent || null);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { if (open) { loadTp(); loadRecap(); setNewEmail(""); setEmailPw(""); setCurPw(""); setNewPw(""); setTpUrl(""); setDisplayName(user?.name || ""); } }, [open, loadTp, loadRecap, user?.name]);

  const saveName = async () => {
    const trimmed = displayName.trim();
    if (!trimmed) { toast.error("Enter your full name"); return; }
    if (trimmed === (user?.name || "")) return;
    setNameBusy(true);
    try {
      await api.post("/auth/change-name", { name: trimmed });
      await refreshUser();
      toast.success("Full name updated");
    } catch (err) { toast.error(apiError(err, "Could not update your name")); }
    finally { setNameBusy(false); }
  };

  const saveEmail = async () => {
    if (!newEmail.trim() || !emailPw) return;
    setEmailBusy(true);
    try {
      const { data } = await api.post("/auth/change-email", { newEmail: newEmail.trim(), password: emailPw });
      localStorage.setItem("spc_email", data.email);
      sessionStorage.removeItem("spc_email");
      await refreshUser();
      toast.success("Email address updated");
      setNewEmail(""); setEmailPw("");
    } catch (err) { toast.error(apiError(err, "Could not update email")); }
    finally { setEmailBusy(false); }
  };

  const savePw = async () => {
    if (!curPw || newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { currentPassword: curPw, newPassword: newPw });
      toast.success("Password changed");
      setCurPw(""); setNewPw("");
    } catch (err) { toast.error(apiError(err, "Could not change password")); }
    finally { setPwBusy(false); }
  };

  const connectTp = async () => {
    if (!tpUrl.trim()) return;
    setTpBusy(true);
    try { const { data } = await api.put("/integrations/trainingpeaks", { url: tpUrl.trim() }); toast.success(`TrainingPeaks connected — ${data.count} workouts`); setTpUrl(""); await loadTp(); }
    catch (err) { toast.error(apiError(err, "Could not connect that calendar")); }
    finally { setTpBusy(false); }
  };
  const disconnectTp = async () => {
    setTpBusy(true);
    try { await api.delete("/integrations/trainingpeaks"); await loadTp(); toast.success("TrainingPeaks disconnected"); }
    catch { toast.error("Could not disconnect"); }
    finally { setTpBusy(false); }
  };

  const subscribeTpp = async () => {
    if (!tppEmail.trim()) return;
    setTppBusy(true);
    try {
      await api.post("/tp-premium/subscribe", { tp_email: tppEmail.trim() });
      await refreshUser();
      setTppEmail("");
      toast.success("TrainingPeaks Premium added — we'll set you up within 24h");
    } catch (err) { toast.error(apiError(err, "Could not add TrainingPeaks Premium")); }
    finally { setTppBusy(false); }
  };
  const cancelTpp = async () => {
    setTppBusy(true);
    try { await api.post("/tp-premium/cancel"); await refreshUser(); toast.success("TrainingPeaks Premium will end at your period end"); }
    catch (err) { toast.error(apiError(err, "Could not cancel")); }
    finally { setTppBusy(false); }
  };

  const toggleRecap = async (next) => {
    setRecapBusy(true);
    try {
      await api.post("/settings/weekly-recap", { enabled: next });
      setRecapEnabled(next);
      toast.success(next ? "Sunday recap turned on" : "Sunday recap turned off");
    } catch (err) { toast.error(apiError(err, "Could not update recap setting")); }
    finally { setRecapBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="account-settings-dialog" className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[92vh] overflow-y-auto p-4 sm:p-6 gap-3">
        <DialogHeader className="mb-1">
          <DialogTitle className="font-display text-lg sm:text-xl uppercase tracking-tight">Account settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Card icon={UserIcon} title="Full name">
            <p className="text-xs text-zinc-400 mb-2">Used across your plans, exports and coach emails.</p>
            <Input data-testid="settings-name-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Jordan Ellis" className="bg-black border-white/15 text-white mb-2" />
            <Button data-testid="settings-save-name" onClick={saveName} disabled={nameBusy || !displayName.trim() || displayName.trim() === (user?.name || "")}
              className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold w-full">
              {nameBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save name"}
            </Button>
          </Card>

          <Card icon={UserIcon} title="Profile photo">
            <p className="text-xs text-zinc-400 mb-3">Shows next to every message in chat. Square photos look best (2MB max).</p>
            <div className="flex items-center gap-3">
              {user?.avatarPath ? (
                <img data-testid="settings-avatar-preview"
                  src={mediaUrl(user.avatarPath)}
                  alt="avatar" className="h-16 w-16 rounded-full object-cover border border-white/15" />
              ) : (
                <div className="h-16 w-16 rounded-full bg-[#1A1A1A] border border-white/15 flex items-center justify-center text-lg font-bold text-white">
                  {((user?.name || email || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("")) || "?"}
                </div>
              )}
              <div className="flex-1 flex flex-col gap-1.5">
                <label className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold w-full cursor-pointer inline-flex items-center justify-center h-9 px-3">
                  {avatarBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (user?.avatarPath ? "Change photo" : "Upload photo")}
                  <input data-testid="settings-avatar-input" type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadAvatar(e.target.files?.[0])} disabled={avatarBusy} />
                </label>
                {user?.avatarPath && (
                  <Button data-testid="settings-avatar-remove" onClick={removeAvatar} disabled={avatarBusy}
                    variant="outline" className="rounded-none bg-transparent border-white/15 text-zinc-300 hover:text-[#FF3B30] hover:border-[#FF3B30]/50 text-[10px] uppercase h-8">Remove photo</Button>
                )}
              </div>
            </div>
          </Card>

          <Card icon={Target} title="Goal weight">
            <p className="text-xs text-zinc-400 mb-2">Target bodyweight. The Weight card's "vs goal" line updates instantly ({weightUnit}).</p>
            <div className="flex items-center gap-2" data-testid="settings-goal-weight">
              <input data-testid="settings-goal-weight-input" type="number" inputMode="decimal" step="0.1"
                value={goalWt} onChange={(e) => setGoalWt(e.target.value)} disabled={goalWtBusy}
                className="flex-1 bg-black border border-white/15 text-white text-base font-display h-10 sm:h-11 px-3" />
              <span className="text-sm font-mono uppercase tracking-wider text-zinc-400 shrink-0 w-8">{weightUnit}</span>
              <button type="button" data-testid="settings-goal-weight-save" onClick={saveGoalWeight} disabled={goalWtBusy}
                className="px-3 h-10 sm:h-11 bg-[#E2E30A] hover:bg-[#c9ca09] disabled:opacity-60 text-black text-[11px] uppercase font-bold tracking-wider inline-flex items-center gap-1.5">
                {goalWtBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
              </button>
            </div>
            {planGoalWeight != null && (
              <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-2">Current goal: {kgToDisplay(planGoalWeight)} {weightUnit}</p>
            )}
          </Card>

          <Card icon={Target} title="Weight tracking cadence">
            <p className="text-xs text-zinc-400 mb-3">How often the Weight card appears in your Daily Snapshot. Some athletes weigh daily, others prefer weekends only, some skip it entirely.</p>
            <div className="grid grid-cols-3 gap-1.5" data-testid="settings-weight-tracking">
              {[
                { k: "daily",  label: "Daily",   sub: "Every day" },
                { k: "weekly", label: "Weekend", sub: "Sat + Sun" },
                { k: "off",    label: "Off",     sub: "Hidden" },
              ].map((o) => (
                <button key={o.k} type="button" data-testid={`settings-weight-tracking-${o.k}`} disabled={wtCadenceBusy}
                  onClick={() => saveWtCadence(o.k)}
                  className={`py-2 text-center border transition-colors ${wtCadence === o.k
                    ? "border-[#E2E30A] text-black bg-[#E2E30A]"
                    : "border-white/15 text-zinc-300 hover:text-white hover:border-white/30"}`}>
                  <span className="block text-[11px] uppercase tracking-wider font-bold">{o.label}</span>
                  <span className={`block text-[9px] font-mono mt-0.5 ${wtCadence === o.k ? "text-black/60" : "text-zinc-500"}`}>{o.sub}</span>
                </button>
              ))}
            </div>
          </Card>

          <Card icon={Camera} title="Progress photos">
            <p className="text-xs text-zinc-300 leading-relaxed mb-3">
              Show a Progress Photo tile inside your Daily Snapshot to log
              body/progress shots. <b className="text-white">Private by
              design</b> — only you (and your coach, if you're linked to one)
              can see them. Not even SPC Performance Nutrition admins can
              view your photos. Turn off at any time; photos stay saved
              unless you delete them.
            </p>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-white font-semibold flex-1">
                {progressOn ? "On — Progress tile shows in Daily Snapshot" : "Off — tile hidden"}
              </p>
              <Button
                data-testid="settings-progress-photos-toggle"
                onClick={() => toggleProgressPhotos(!progressOn)}
                disabled={progressBusy}
                variant="outline"
                className="rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase font-semibold min-w-[92px]"
              >
                {progressBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (progressOn ? "Turn off" : "Turn on")}
              </Button>
            </div>
          </Card>

          <Card icon={Ruler} title="Units & display">
            <p className="text-xs text-zinc-400 mb-3">Pick how distances and bodyweight show up across your Tracker, plans and exports.</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-300 font-bold mb-1.5">Distance</p>
            <div className="grid grid-cols-2 gap-1.5 mb-4" data-testid="settings-distance-unit">
              {[
                { k: "mi", label: "Miles" },
                { k: "km", label: "Kilometres" },
              ].map((o) => (
                <button key={o.k} type="button" data-testid={`settings-distance-${o.k}`} disabled={unitsBusy}
                  onClick={() => o.k !== distanceUnit && saveUnits({ distanceUnit: o.k })}
                  className={`py-2.5 text-[11px] uppercase tracking-wider font-semibold border transition-colors ${distanceUnit === o.k
                    ? "border-[#E2E30A] text-black bg-[#E2E30A]"
                    : "border-white/15 text-zinc-300 hover:text-white hover:border-white/30"}`}>
                  {o.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-300 font-bold mb-1.5">Bodyweight</p>
            <div className="grid grid-cols-3 gap-1.5" data-testid="settings-weight-unit">
              {[
                { k: "kg", label: "Kilograms" },
                { k: "lb", label: "Pounds" },
                { k: "st", label: "Stone" },
              ].map((o) => (
                <button key={o.k} type="button" data-testid={`settings-weight-${o.k}`} disabled={unitsBusy}
                  onClick={() => o.k !== weightUnit && saveUnits({ weightUnit: o.k })}
                  className={`py-2.5 text-[11px] uppercase tracking-wider font-semibold border transition-colors ${weightUnit === o.k
                    ? "border-[#E2E30A] text-black bg-[#E2E30A]"
                    : "border-white/15 text-zinc-300 hover:text-white hover:border-white/30"}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </Card>

          <Card icon={Mail} title="Email address">
            <p className="text-xs text-zinc-400 mb-2">Signed in as <span className="text-white">{email}</span></p>
            <Input data-testid="settings-new-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="New email address" className="bg-black border-white/15 text-white mb-2" />
            <Input data-testid="settings-email-password" type="password" value={emailPw} onChange={(e) => setEmailPw(e.target.value)} placeholder="Confirm your password" className="bg-black border-white/15 text-white mb-2" />
            <Button data-testid="settings-save-email" onClick={saveEmail} disabled={emailBusy || !newEmail.trim() || !emailPw}
              className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold w-full">
              {emailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update email"}
            </Button>
          </Card>

          <Card icon={Lock} title="Password">
            <Input data-testid="settings-current-password" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} placeholder="Current password" className="bg-black border-white/15 text-white mb-2" />
            <Input data-testid="settings-new-password" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (min 8 characters)" className="bg-black border-white/15 text-white mb-2" />
            <Button data-testid="settings-save-password" onClick={savePw} disabled={pwBusy || !curPw || newPw.length < 8}
              className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold w-full">
              {pwBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change password"}
            </Button>
          </Card>

          <Card icon={Activity} title="TrainingPeaks calendar">
            {tp.connected ? (
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#34C759]">Connected</p>
                <Button data-testid="settings-tp-disconnect" onClick={disconnectTp} disabled={tpBusy} variant="outline"
                  className="rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase">
                  {tpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disconnect"}
                </Button>
              </div>
            ) : (
              <div>
                <TrainingPeaksHelp />
                <Input data-testid="settings-tp-url" value={tpUrl} onChange={(e) => setTpUrl(e.target.value)} placeholder="webcal://www.trainingpeaks.com/ical/XXXX.ics" className="bg-black border-white/15 text-white text-xs mb-2" />
                <Button data-testid="settings-tp-connect" onClick={connectTp} disabled={tpBusy || !tpUrl.trim()}
                  className="rounded-none bg-[#4C9AFF] hover:bg-[#3a86e6] text-black text-xs uppercase font-semibold w-full">
                  {tpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect TrainingPeaks"}
                </Button>
              </div>
            )}
          </Card>

          <Card icon={MailCheck} title="Sunday recap email">
            <p className="text-xs text-zinc-300 leading-relaxed mb-3">
              Every Sunday night we email you a personalised week in numbers —
              sessions ticked, streak, RPE trend and one thing to focus on next
              week.
            </p>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm text-white font-semibold">
                  {recapEnabled ? "On — you'll get the recap on Sunday" : "Off — no Sunday emails"}
                </p>
                {recapLastSent && (
                  <p className="text-[11px] text-zinc-400 mt-0.5">
                    Last sent {new Date(recapLastSent).toLocaleDateString(undefined,
                      { day: "numeric", month: "short" })}
                  </p>
                )}
              </div>
              <Button
                data-testid="settings-recap-toggle"
                onClick={() => toggleRecap(!recapEnabled)}
                disabled={recapBusy}
                variant="outline"
                className="rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase font-semibold min-w-[92px]"
              >
                {recapBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (recapEnabled ? "Turn off" : "Turn on")}
              </Button>
            </div>
          </Card>

          <Card icon={Star} title="TrainingPeaks Premium — £9.99/mo">
            {tppActive ? (
              <div data-testid="tpp-active">
                <p className="text-sm text-[#34C759]">
                  {tpp.status === "pending" && "Requested — we're setting you up (within 24h)"}
                  {tpp.status === "active" && "Active — you're a Premium athlete"}
                  {tpp.status === "cancelling" && "Ending at your period end"}
                </p>
                {tpp.tpEmail && <p className="text-[11px] text-zinc-400 mt-1">TrainingPeaks account: {tpp.tpEmail}</p>}
                {tpp.status !== "cancelling" && (
                  <Button data-testid="tpp-cancel" onClick={cancelTpp} disabled={tppBusy} variant="outline"
                    className="mt-3 rounded-none border-white/20 bg-transparent hover:bg-white/5 text-white text-xs uppercase">
                    {tppBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel add-on"}
                  </Button>
                )}
              </div>
            ) : (
              <div data-testid="tpp-signup">
                <p className="text-xs text-zinc-400 leading-relaxed mb-1">No TrainingPeaks Premium? Add it here and we'll set you up as a Premium athlete inside our TrainingPeaks coaching account — Calendar Sync included. Billed as an extra £9.99/mo line on your SPC subscription.</p>
                <p className="text-[10px] text-zinc-300 mb-2">We'll add you within 24h — you'll get an invite email from TrainingPeaks to accept. Cancel anytime; it ends at your period end.</p>
                <Input data-testid="tpp-email" type="email" value={tppEmail} onChange={(e) => setTppEmail(e.target.value)} placeholder="Your TrainingPeaks account email" className="bg-black border-white/15 text-white text-xs mb-2" />
                <Button data-testid="tpp-subscribe" onClick={subscribeTpp} disabled={tppBusy || !tppEmail.trim()}
                  className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold w-full">
                  {tppBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Premium — £9.99/mo"}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
